// =============================================================================
// DiscountApprovalsPage — owner sign-off queue for over-cap discounts
// =============================================================================
// A salesperson asked to discount a draft order beyond the company cap. Each row
// shows who asked, for how much, on which order, and why. Two actions: Approve
// (optionally at a different ceiling than requested) or Reject (note required).
// Approving unblocks the order's confirm up to the granted ceiling; the
// salesperson then confirms + takes payment from the order page.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DiscountApprovalStatus } from '@kalachand/shared';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { formatPct } from '../../lib/discount';

interface DiscountApproval {
  id: string;
  status: DiscountApprovalStatus;
  orderGross: string;
  requestedDiscountTotal: string;
  requestedPct: string;
  capPct: string;
  approvedPct: string | null;
  reason: string | null;
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
  requestedBy: { id: string; name: string };
  decidedBy: { id: string; name: string } | null;
  order: {
    id: string;
    number: string;
    status: string;
    total: string;
    discountTotal: string;
    vatExempt: boolean;
    shop: { id: string; code: string; name: string };
    // The sales girl this order is attributed to (drives her commission). May
    // differ from requestedBy when an HQ/ops user builds the order for a branch.
    salesperson: { id: string; name: string } | null;
    customer: { id: string; name: string; companyName: string | null; type: string; phone: string };
    lines: Array<{
      id: string;
      qty: number;
      unitPrice: string;
      discount: string;
      lineTotal: string;
      productSnapshot: { name?: string; sku?: string } | null;
    }>;
  };
  // Attached server-side: the customer's lifetime stats + this discount's margin.
  customerStats: { orderCount: number; totalSpent: string };
  margin: {
    costKnown: boolean;
    netRevenue: number;
    cost: number;
    profit: number;
    marginPct: number;
  };
}

function custName(c: DiscountApproval['order']['customer']): string {
  return c.type === 'company' ? (c.companyName ?? c.name) : c.name;
}
function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}
const pct = (frac: string | number): string => formatPct(Number(frac) * 100);
const lineName = (l: DiscountApproval['order']['lines'][number]): string =>
  l.productSnapshot?.name ?? 'Item';

// A compact "what's being bought" + "who's buying" + "what's left after the
// discount" block — the context the owner needs to decide on the spot.
function ApprovalContext({ a }: { a: DiscountApproval }): JSX.Element {
  const returning = a.customerStats.orderCount > 0;
  const m = a.margin;
  const marginTone = !m.costKnown
    ? 'text-ink-3'
    : m.profit <= 0
      ? 'text-negative'
      : m.marginPct < 0.15
        ? 'text-warning'
        : 'text-positive';
  return (
    <div className="space-y-3">
      {/* Items */}
      <div className="rounded border border-rule overflow-hidden">
        <div className="bg-paper-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-3 font-medium">
          {a.order.lines.length} item{a.order.lines.length === 1 ? '' : 's'}
        </div>
        <ul className="divide-y divide-rule-2">
          {a.order.lines.map((l) => (
            <li
              key={l.id}
              className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm"
            >
              <span className="text-ink">
                {l.qty} × {lineName(l)}
                {Number(l.discount) > 0 && (
                  <span className="text-warning text-xs">
                    {' '}
                    · −<Money value={l.discount} muted />
                  </span>
                )}
              </span>
              <Money value={l.lineTotal} muted />
            </li>
          ))}
        </ul>
      </div>
      {/* Customer + margin side by side */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded border border-rule px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium">
            Customer
          </div>
          <div className="text-ink mt-0.5">{custName(a.order.customer)}</div>
          <div className="text-xs text-ink-3">{a.order.customer.phone}</div>
          <div className="text-xs mt-1">
            {returning ? (
              <span className="text-positive">
                {a.customerStats.orderCount} past order{a.customerStats.orderCount === 1 ? '' : 's'}{' '}
                · <Money value={a.customerStats.totalSpent} muted /> lifetime
              </span>
            ) : (
              <span className="text-ink-3">First-time customer</span>
            )}
          </div>
        </div>
        <div className="rounded border border-rule px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium">
            After this discount
          </div>
          {m.costKnown ? (
            <>
              <div className={`mt-0.5 font-medium ${marginTone}`}>
                Profit <Money value={m.profit.toFixed(2)} /> · {(m.marginPct * 100).toFixed(0)}%
                margin
              </div>
              <div className="text-xs text-ink-3 mt-0.5">
                on <Money value={m.netRevenue.toFixed(2)} muted /> net
                {a.order.vatExempt ? ' (VAT-exempt)' : ''}
              </div>
            </>
          ) : (
            <div className="text-xs text-ink-3 mt-0.5">
              Cost price not set on all items — margin unknown.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const statusTone: Record<DiscountApprovalStatus, 'warning' | 'positive' | 'negative' | 'neutral'> =
  {
    PENDING: 'warning',
    APPROVED: 'positive',
    REJECTED: 'negative',
    CANCELLED: 'neutral',
  };

export function DiscountApprovalsPage(): JSX.Element {
  const qc = useQueryClient();
  const { perms } = useAuth();
  // Approvers (owner / admin) act on every request; everyone else is a
  // salesperson viewing their own requests read-only (the API scopes the list).
  const canApprove = perms.has('discounts.apply_above_ceiling');
  const [filter, setFilter] = useState<'PENDING' | 'ALL'>('PENDING');
  const [approveTarget, setApproveTarget] = useState<DiscountApproval | null>(null);
  const [approvePct, setApprovePct] = useState(''); // percent units (e.g. "8")
  const [approveNote, setApproveNote] = useState('');
  const [rejectTarget, setRejectTarget] = useState<DiscountApproval | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['discount-approvals', filter],
    queryFn: () =>
      apiGet<DiscountApproval[]>(
        '/discount-approvals',
        filter === 'PENDING' ? { status: 'PENDING' } : {},
      ),
    // New requests surface without a manual refresh; refresh on focus too.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['discount-approvals'] });
    qc.invalidateQueries({ queryKey: ['discount-approvals-pending-count'] });
    qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
  };

  const approve = useMutation({
    mutationFn: ({ id, approvedPct, note }: { id: string; approvedPct?: number; note?: string }) =>
      apiPost(`/discount-approvals/${id}/approve`, { approvedPct, note: note || null }),
    onSuccess: () => {
      invalidate();
      setApproveTarget(null);
      setApprovePct('');
      setApproveNote('');
      setErr(null);
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiPost(`/discount-approvals/${id}/reject`, { note }),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectNote('');
      setErr(null);
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const rows = list.data ?? [];
  const pendingRows = rows.filter((r) => r.status === 'PENDING');
  const atStake = pendingRows.reduce((acc, r) => acc + Number(r.requestedDiscountTotal), 0);

  return (
    <PageScaffold
      scroll={false}
      title={canApprove ? 'Discount approvals' : 'My discount requests'}
      subtitle={
        canApprove
          ? 'Salespeople asking to discount a draft order beyond the cap. Approve (optionally at a different ceiling) or reject — the salesperson then sets delivery/pickup details, confirms the order, and takes payment.'
          : "Draft orders you've sent for owner sign-off. Once approved, go to the order page, set the pickup/delivery details (skipped while awaiting approval), then confirm and take payment."
      }
      actions={
        <div className="inline-flex rounded border border-rule overflow-hidden">
          {(['PENDING', 'ALL'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                'px-3 py-1.5 text-sm font-medium transition ' +
                (filter === f ? 'bg-accent text-white' : 'bg-paper-2 text-ink-3 hover:text-ink')
              }
            >
              {f === 'PENDING' ? 'Pending' : 'All'}
            </button>
          ))}
        </div>
      }
    >
      <div className="shrink-0 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg bg-ink text-paper-2 p-6">
          <div className="text-[10px] uppercase tracking-wider text-ink-4 font-semibold">
            {canApprove ? 'Awaiting decision' : 'Awaiting owner'}
          </div>
          <div className="kc-serif text-5xl text-paper-2 mt-2">{pendingRows.length}</div>
          <div className="text-xs text-ink-4 mt-3">
            request{pendingRows.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            {canApprove ? 'Discount at stake' : 'Discount requested'}
          </div>
          <Money value={atStake.toFixed(2)} className="kc-serif text-3xl text-ink mt-2 block" />
          <div className="text-xs text-ink-3 mt-2">Sum of pending requested discounts</div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Oldest pending
          </div>
          <div className="kc-serif text-3xl text-ink mt-2">
            {pendingRows.length === 0
              ? '—'
              : `${daysAgo(pendingRows[pendingRows.length - 1]!.createdAt)}d`}
          </div>
          <div className="text-xs text-ink-3 mt-2">since requested</div>
        </div>
      </div>

      {err && (
        <Card className="shrink-0 border-negative/40 bg-negative-soft">
          <div className="text-sm text-negative">{err}</div>
        </Card>
      )}

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={filter === 'PENDING' ? 'Nothing to approve' : 'No requests yet'}
            description="Discount requests above the cap will appear here for sign-off."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Order</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Requested by</th>
                <th className="px-6 py-2 font-medium text-right">Discount</th>
                <th className="px-6 py-2 font-medium">Reason</th>
                <th className="px-6 py-2 font-medium text-right">{canApprove ? 'Actions' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-rule-2 hover:bg-paper-2 align-top">
                  <td className="px-6 py-3">
                    <Link
                      to={`/orders/${r.order.id}`}
                      className="kc-mono text-ink hover:text-accent"
                    >
                      {r.order.number}
                    </Link>
                    <div className="text-[10px] text-ink-3 mt-0.5">
                      {r.order.shop.code} · {r.order.status.toLowerCase()}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-ink">{custName(r.order.customer)}</div>
                    <div className="text-xs text-ink-3">
                      {r.customerStats.orderCount > 0 ? (
                        <span className="text-positive">
                          {r.customerStats.orderCount} order
                          {r.customerStats.orderCount === 1 ? '' : 's'} ·{' '}
                          <Money value={r.customerStats.totalSpent} muted />
                        </span>
                      ) : (
                        'first-time'
                      )}
                    </div>
                    <div className="text-[11px] text-ink-3 mt-0.5">
                      {r.order.lines.length} item{r.order.lines.length === 1 ? '' : 's'} · order{' '}
                      <Money value={r.orderGross} muted />
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-ink">{r.requestedBy.name}</div>
                    {r.order.salesperson && r.order.salesperson.id !== r.requestedBy.id && (
                      <div className="text-[11px] text-ink-2 mt-0.5">
                        Sold by {r.order.salesperson.name}
                      </div>
                    )}
                    <div className="text-[10px] text-ink-3 mt-0.5">
                      {daysAgo(r.createdAt) === 0 ? 'today' : `${daysAgo(r.createdAt)}d ago`}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="font-medium text-ink">{pct(r.requestedPct)}</div>
                    <div className="text-xs text-ink-3">
                      <Money value={r.requestedDiscountTotal} muted /> · cap {pct(r.capPct)}
                    </div>
                    {r.status !== 'PENDING' && (
                      <div className="mt-1">
                        <StatusPill tone={statusTone[r.status]}>
                          {r.status.toLowerCase()}
                        </StatusPill>
                        {r.status === 'APPROVED' && r.approvedPct != null && (
                          <div className="text-[10px] text-ink-3 mt-0.5">
                            at {pct(r.approvedPct)}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 max-w-[16rem]">
                    {r.reason ? (
                      <span className="text-ink-2 italic">“{r.reason}”</span>
                    ) : (
                      <span className="text-ink-4">—</span>
                    )}
                    {r.status !== 'PENDING' && r.decisionNote && (
                      <div className="text-[11px] text-ink-3 mt-1">
                        {r.decidedBy?.name}: {r.decisionNote}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {canApprove ? (
                      r.status === 'PENDING' ? (
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setRejectTarget(r);
                              setRejectNote('');
                              setErr(null);
                            }}
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setApproveTarget(r);
                              setApprovePct((Number(r.requestedPct) * 100).toString());
                              setApproveNote('');
                              setErr(null);
                            }}
                          >
                            Approve
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-3">
                          {r.decidedBy?.name}
                          {r.decidedAt &&
                            ` · ${new Date(r.decidedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}`}
                        </span>
                      )
                    ) : r.status === 'PENDING' ? (
                      <span className="text-xs text-warning">Awaiting owner</span>
                    ) : r.status === 'APPROVED' ? (
                      <Link
                        to={`/orders/${r.order.id}`}
                        className="text-xs text-positive hover:underline"
                      >
                        Approved — confirm →
                      </Link>
                    ) : r.status === 'REJECTED' ? (
                      <span className="text-xs text-negative">Rejected</span>
                    ) : (
                      <span className="text-xs text-ink-3">Cancelled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>

      {/* Approve modal — optional adjusted ceiling + note */}
      {approveTarget && (
        <Modal
          onClose={() => setApproveTarget(null)}
          title="Approve discount"
          subtitle={`${approveTarget.order.number} · ${custName(approveTarget.order.customer)}`}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setApproveTarget(null)}>
                Cancel
              </Button>
              <Button
                loading={approve.isPending}
                onClick={() => {
                  const raw = approvePct.trim();
                  const asPct = raw === '' ? undefined : Number(raw) / 100;
                  if (asPct != null && (!Number.isFinite(asPct) || asPct < 0 || asPct > 1)) {
                    setErr('Approved discount must be between 0 and 100%.');
                    return;
                  }
                  approve.mutate({ id: approveTarget.id, approvedPct: asPct, note: approveNote });
                }}
              >
                Approve
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm">
            <ApprovalContext a={approveTarget} />
            <div className="bg-paper-2 rounded p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-3">Requested</span>
                <span className="text-ink font-medium">
                  {pct(approveTarget.requestedPct)} (
                  <Money value={approveTarget.requestedDiscountTotal} muted />)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Staff cap</span>
                <span>{pct(approveTarget.capPct)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Requested by</span>
                <span>{approveTarget.requestedBy.name}</span>
              </div>
              {approveTarget.order.salesperson &&
                approveTarget.order.salesperson.id !== approveTarget.requestedBy.id && (
                  <div className="flex justify-between">
                    <span className="text-ink-3">Sold by</span>
                    <span>{approveTarget.order.salesperson.name}</span>
                  </div>
                )}
              {approveTarget.reason && (
                <div className="text-[11px] text-ink-3 pt-1 italic">“{approveTarget.reason}”</div>
              )}
            </div>
            <Input
              label="Approve at (%)"
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={approvePct}
              onChange={(e) => setApprovePct(e.target.value)}
              placeholder={(Number(approveTarget.requestedPct) * 100).toString()}
            />
            <p className="text-[11px] text-ink-3 -mt-1">
              Defaults to the requested {pct(approveTarget.requestedPct)}. Lower it to grant a
              smaller discount — the order can only confirm at or below this ceiling.
            </p>
            <Input
              label="Note (optional)"
              value={approveNote}
              onChange={(e) => setApproveNote(e.target.value)}
              placeholder="e.g. one-off — don't make it a habit"
            />
          </div>
        </Modal>
      )}

      {/* Reject modal — note required */}
      {rejectTarget && (
        <Modal
          onClose={() => setRejectTarget(null)}
          title="Reject discount request"
          subtitle={`${rejectTarget.order.number} · ${custName(rejectTarget.order.customer)}`}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => reject.mutate({ id: rejectTarget.id, note: rejectNote })}
                loading={reject.isPending}
                disabled={!rejectNote.trim()}
              >
                Reject
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm">
            <ApprovalContext a={rejectTarget} />
            <div className="bg-paper-2 rounded p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-3">Requested</span>
                <span className="text-ink font-medium">{pct(rejectTarget.requestedPct)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Requested by</span>
                <span>{rejectTarget.requestedBy.name}</span>
              </div>
              {rejectTarget.order.salesperson &&
                rejectTarget.order.salesperson.id !== rejectTarget.requestedBy.id && (
                  <div className="flex justify-between">
                    <span className="text-ink-3">Sold by</span>
                    <span>{rejectTarget.order.salesperson.name}</span>
                  </div>
                )}
            </div>
            <p className="text-xs text-ink-3">
              The salesperson will see the rejection on the order and must lower the discount to the{' '}
              {pct(rejectTarget.capPct)} cap (or request again).
            </p>
            <Input
              label="Reason"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. margin too thin on this model"
              autoFocus
            />
          </div>
        </Modal>
      )}
    </PageScaffold>
  );
}
